import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import {
  AugmentedCreateGrowthProfileOverrideInput,
  GrowthProfileOverrideKind,
} from '@/types/schema';
import { formatCurrency } from '@/utils/formatting/currency';
import { formatPercent } from '@/utils/formatting/percent';
import { getNodes } from '@/utils/graphqlUtils';

import {
  GrowthProfileModal_AssetClassFragment,
  GrowthProfileModal_GrowthProfileFragment,
  GrowthProfileModal_GrowthProfileOverrideFragment,
} from './graphql/GrowthProfileModal.generated';
import {
  CreateGrowthProfileMutationVariables,
  UpdateGrowthProfileMutationVariables,
} from './graphql/GrowthProfileModalMutations.generated';
import {
  CustomEntityGrowthCustomizationType,
  getDefaultGrowthProfileModalFormShape,
  GrowthProfileCustomGrowthOverrideRowData,
  GrowthProfileModalFormShape,
  GrowthProfileModalGrowthOverride,
} from './GrowthProfileModal.types';
import { TaxDrag_DefaultsFragment } from './taxDrag/TaxDrag.generated';
import {
  mapIncomingTaxDragToForm,
  mapTaxDragToNestedMutationInput,
} from './taxDrag/TaxDrag.mappers';

/**
 * Determines the customization type based on the available growth profile override fields
 */
export function getCustomEntityGrowthModalCustomizationType({
  kind,
  entityGrowthBeforeFutureValuation,
  entityGrowthAfterFutureValuation,
  entityFutureValuationValue,
  entityFutureValuationYear,
  customGrowthRate,
}: Partial<GrowthProfileModalGrowthOverride>): CustomEntityGrowthCustomizationType | null {
  if (kind === GrowthProfileOverrideKind.Asset) {
    return CustomEntityGrowthCustomizationType.None;
  }

  if (entityGrowthBeforeFutureValuation && entityGrowthAfterFutureValuation) {
    return CustomEntityGrowthCustomizationType.GrowthRateAndFutureValuation;
  }

  if (entityFutureValuationValue && entityFutureValuationYear) {
    return CustomEntityGrowthCustomizationType.FutureValuation;
  }

  if (customGrowthRate) {
    return CustomEntityGrowthCustomizationType.GrowthRate;
  }

  return null;
}

function getGrowthProfileInput(data: GrowthProfileModalFormShape) {
  return {
    displayName: data.displayName,
    exemptionGrowthRate: data.exemptionGrowthRate,
    growthType: data.growthType,
    assetGrowthReturn: data.assetGrowthReturn,
  };
}

function getGrowthProfileOverrideInput(
  override: GrowthProfileModalGrowthOverride,
  householdID: string
): AugmentedCreateGrowthProfileOverrideInput | null {
  const {
    // these fields are only needed on the client
    id: _id,
    customizationType: _customizationType,
    displayName: _displayName,
    defaultGrowthRate: _defaultGrowthRate,
    entity,
    proposal,
    entityOrProposalId,
    assetClass,
    kind,
    ...rest
  } = override;

  // don't include overrides w/o custom growth rates
  if (
    override.kind === GrowthProfileOverrideKind.Asset &&
    !override.customGrowthRate
  ) {
    return null;
  }

  const idToUse = entityOrProposalId ?? entity?.id ?? proposal?.id;

  // Skip overrides that reference entities or proposals that no longer exist
  if (
    (kind === GrowthProfileOverrideKind.Entity && !idToUse) ||
    (kind === GrowthProfileOverrideKind.Proposal && !idToUse)
  ) {
    return null;
  }

  return {
    create: {
      householdID,
      entityID: kind === GrowthProfileOverrideKind.Entity ? idToUse : undefined,
      proposalID:
        kind === GrowthProfileOverrideKind.Proposal ? idToUse : undefined,
      assetClassID: assetClass?.id,
      kind,
      ...rest,
    },
  };
}

export function mapFormDataToCreateGrowthProfileInput(
  data: GrowthProfileModalFormShape,
  householdID: string,
  includeTaxDrag = false
): CreateGrowthProfileMutationVariables {
  const taxDragInput = includeTaxDrag
    ? mapTaxDragToNestedMutationInput(data.taxDrag, householdID, 'create')
    : undefined;
  const input = {
    create: {
      householdID,
      ...getGrowthProfileInput(data),
    },
    withGrowthProfileOverrides: compact(
      data.growthProfileOverrides.map<AugmentedCreateGrowthProfileOverrideInput | null>(
        (override) => getGrowthProfileOverrideInput(override, householdID)
      )
    ),
    ...(taxDragInput && {
      withTaxDragConfig: taxDragInput.withTaxDragConfig,
      withIncomeTaxPaymentConfig: taxDragInput.withIncomeTaxPaymentConfig,
      withTaxDragOverrides: taxDragInput.withTaxDragOverrides,
    }),
  };

  return {
    input,
  };
}

export function mapFormDataToUpdateGrowthProfileInput(
  data: GrowthProfileModalFormShape,
  householdID: string,
  growthProfileId: string,
  includeTaxDrag = false
): UpdateGrowthProfileMutationVariables {
  const taxDragInput = includeTaxDrag
    ? mapTaxDragToNestedMutationInput(data.taxDrag, householdID)
    : undefined;
  const input = {
    id: growthProfileId,
    update: {
      ...getGrowthProfileInput(data),
      clearGrowthProfileOverrides: true,
      ...taxDragInput?.update,
    },
    withGrowthProfileOverrides: compact(
      data.growthProfileOverrides.map<AugmentedCreateGrowthProfileOverrideInput | null>(
        (override) => getGrowthProfileOverrideInput(override, householdID)
      )
    ),
    ...(taxDragInput && {
      withTaxDragConfig: taxDragInput.withTaxDragConfig,
      updateTaxDragConfig: taxDragInput.updateTaxDragConfig,
      withIncomeTaxPaymentConfig: taxDragInput.withIncomeTaxPaymentConfig,
      updateIncomeTaxPaymentConfig: taxDragInput.updateIncomeTaxPaymentConfig,
      withTaxDragOverrides: taxDragInput.withTaxDragOverrides,
    }),
  };

  return {
    input,
  };
}

export function private_mapIncomingEntityOverridesToForm(
  overrides: GrowthProfileModal_GrowthProfileOverrideFragment[]
): GrowthProfileModalGrowthOverride[] {
  return overrides
    .filter(
      ({ kind, entity, proposal }) =>
        (kind === GrowthProfileOverrideKind.Entity && !!entity) ||
        (kind === GrowthProfileOverrideKind.Proposal && !!proposal)
    )
    .map((override, index) => {
      const { id: _id, ...profileWithoutId } = override;
      const customizationType =
        getCustomEntityGrowthModalCustomizationType(profileWithoutId) ||
        CustomEntityGrowthCustomizationType.None;

      let displayName = 'Unknown';

      if (
        override.kind === GrowthProfileOverrideKind.Proposal &&
        override.proposal
      ) {
        const proposal = override.proposal;
        displayName = proposal.displayName;
      } else if (override.entity?.subtype) {
        displayName = override.entity.subtype.displayName;
      }

      return {
        ...override,
        id: index,
        customizationType,
        displayName,
      };
    });
}

function mapIncomingAssetClassesToForm(
  assetClasses: GrowthProfileModal_AssetClassFragment[],
  overrides: GrowthProfileModal_GrowthProfileOverrideFragment[],
  entityOverridesLength: number
): GrowthProfileModalGrowthOverride[] {
  return assetClasses.map<GrowthProfileModalGrowthOverride>(
    (assetClass, index) => {
      const matchingOverride = overrides.find(
        (override) => override.assetClass?.id === assetClass.id
      );

      return {
        id: index + entityOverridesLength,
        kind: GrowthProfileOverrideKind.Asset,
        customizationType: CustomEntityGrowthCustomizationType.None,
        defaultGrowthRate: assetClass.growthRate,
        customGrowthRate: matchingOverride?.customGrowthRate,
        displayName: assetClass.displayName,
        assetClass,
      };
    }
  );
}

export function mapIncomingDataToForm(
  growthProfile: GrowthProfileModal_GrowthProfileFragment | null | undefined,
  assetClasses: GrowthProfileModal_AssetClassFragment[],
  primaryClientState?: string | null,
  taxDragDefaults?: TaxDrag_DefaultsFragment
): GrowthProfileModalFormShape {
  const overrides = getNodes(growthProfile?.growthProfileOverrides);

  const entityAndProposalOverrides =
    private_mapIncomingEntityOverridesToForm(overrides);

  const assetClassOverrides = mapIncomingAssetClassesToForm(
    assetClasses,
    overrides,
    entityAndProposalOverrides.length
  );

  const defaultGrowthProfileModalFormShape =
    getDefaultGrowthProfileModalFormShape();

  return {
    displayName:
      growthProfile?.displayName ??
      defaultGrowthProfileModalFormShape.displayName,
    exemptionGrowthRate:
      growthProfile?.exemptionGrowthRate ??
      defaultGrowthProfileModalFormShape.exemptionGrowthRate,
    growthType:
      growthProfile?.growthType ??
      defaultGrowthProfileModalFormShape.growthType,
    assetGrowthReturn:
      growthProfile?.assetGrowthReturn ??
      defaultGrowthProfileModalFormShape.assetGrowthReturn,
    growthProfileOverrides: [
      ...entityAndProposalOverrides,
      ...assetClassOverrides,
    ],
    taxDrag: mapIncomingTaxDragToForm(
      growthProfile?.taxDragConfig,
      getNodes(growthProfile?.taxDragOverrides),
      primaryClientState,
      taxDragDefaults,
      growthProfile?.incomeTaxPaymentConfig
    ),
  };
}

export function mapGrowthOverrideToTableRow({
  id,
  entity,
  assetClass,
  customGrowthRate,
  entityFutureValuationYear,
  entityFutureValuationValue,
  entityGrowthBeforeFutureValuation,
  entityGrowthAfterFutureValuation,
  displayName: explicitDisplayName,
  kind,
}: GrowthProfileModalGrowthOverride): GrowthProfileCustomGrowthOverrideRowData {
  if (assetClass) {
    return {
      id,
      kind: GrowthProfileOverrideKind.Asset,
      displayName: assetClass?.displayName ?? 'Unknown',
      displayType: 'Asset class',
      futureValuationValue: EMPTY_CONTENT_HYPHEN,
      futureValuationYear: undefined,
      growthRateValue: `${formatPercent(
        customGrowthRate ?? new Decimal(0),
        2
      )}%`,
      growthRateSubtitle: undefined,
    };
  }

  const futureValuationValue = entityFutureValuationValue
    ? formatCurrency(entityFutureValuationValue)
    : EMPTY_CONTENT_HYPHEN;

  const futureValuationYear: string | undefined =
    entityFutureValuationValue && entityFutureValuationYear
      ? `at year ${entityFutureValuationYear.toString()}`
      : undefined;

  let growthRateValue = customGrowthRate
    ? `${formatPercent(customGrowthRate, 2)}%`
    : EMPTY_CONTENT_HYPHEN;
  let growthRateSubtitle: string | undefined = undefined;

  if (entityGrowthBeforeFutureValuation && entityGrowthAfterFutureValuation) {
    growthRateValue = `${formatPercent(entityGrowthBeforeFutureValuation, 2)}% / ${formatPercent(entityGrowthAfterFutureValuation, 2)}%`;
    growthRateSubtitle = 'before/after future valuation';
  }

  let displayType = 'Entity';
  if (kind === GrowthProfileOverrideKind.Proposal) {
    displayType = 'Proposal';
  }

  const finalDisplayName =
    explicitDisplayName || entity?.subtype?.displayName || 'Unknown';

  return {
    id,
    kind: kind || GrowthProfileOverrideKind.Entity,
    displayName: finalDisplayName,
    displayType,
    futureValuationValue,
    futureValuationYear,
    growthRateValue,
    growthRateSubtitle,
  };
}
